import React from 'react'
import { Button } from '@material-ui/core'
import { AuthContainer } from 'containers'
import { PrimaryButton, Modal, QueryStatusAlert, TextBox, Loader, SelectQuery } from 'components'
import { MAX_ALLOWED_QUERIES } from 'constants/saveLoadQuery'
import { invalidSpecialCharacters, invalidStartEndCharacters } from 'utils/utilities'
import { useValidateFields } from 'utils/forms'
import { useGetQueries, useDeleteQuery, useSaveQuery } from 'hooks'

export type SaveQueryButtonProps = {
  isEnabled: boolean
  queryToSave: {} | undefined
  stateToWatch: any
  reportName: string
}

export const SaveQueryButton = (props: SaveQueryButtonProps) => {
  const { isEnabled, queryToSave, stateToWatch, reportName } = props
  const { user, selectedSupplier } = AuthContainer.useContainer()

  const [saveOpen, setSaveOpen] = React.useState(false)
  const [overrideOpen, setOverrideOpen] = React.useState(false)
  const [saveQueryTitle, setSaveQueryTitle] = React.useState('')
  const [selectedQueryTitle, setSelectedQueryTitle] = React.useState('')

  const [initialLoad, setInitialLoad] = React.useState(true)
  const [querySaved, setQuerySaved] = React.useState(true)

  const isSaveEnabled = isEnabled && !!queryToSave && !querySaved

  React.useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false)
    } else {
      setQuerySaved(false)
    }
    // eslint-disable-next-line
  }, [stateToWatch])

  const { getQueriesStatus, getQueriesData, getQueriesError, fetchQueriesData } = useGetQueries({
    vendorNumber: selectedSupplier?.oracleId ?? '',
    userId: user?.googleId ?? '',
    reportName: reportName,
  })
  const { getQuerySaveStatus, getQuerySaveError, mutateSaveQuery, resetSaveQuery } = useSaveQuery()
  const { getDeleteQueryStatus, getDeleteQueryError, mutateDeleteQuery, resetDeleteQuery } = useDeleteQuery()

  const handleOpen = () => {
    if (getQueriesData && getQueriesData.length >= MAX_ALLOWED_QUERIES) {
      setOverrideOpen(true)
    } else {
      setSaveOpen(true)
    }
  }

  const handleClose = () => {
    setSaveQueryTitle('')
    setSelectedQueryTitle('')
    setOverrideOpen(false)
    setSaveOpen(false)
    resetValidations()
    resetDeleteQuery()
    resetSaveQuery()
  }

  const handleSave = async () => {
    mutateSaveQuery(
      {
        vendorNumber: selectedSupplier?.oracleId ?? '',
        userId: user?.googleId ?? '',
        queryData: {
          querytitle: saveQueryTitle,
          query: queryToSave ?? {},
        },
        reportName: reportName,
      },
      {
        onSuccess: () => {
          setQuerySaved(true)
          handleClose()
          fetchQueriesData()
        },
      }
    )
  }

  const handleOverride = () => {
    mutateDeleteQuery(
      {
        vendorNumber: selectedSupplier?.oracleId ?? '',
        userId: user?.googleId ?? '',
        queryTitle: selectedQueryTitle,
        reportName: reportName,
      },
      { onSuccess: () => handleSave() }
    )
  }

  const { validateField, setValidationLogic, getFieldErrorText, resetValidations } =
    useValidateFields<{ saveQueryTitle: string }>()
  const validationLogin = setValidationLogic(() => [
    { condition: !saveQueryTitle },
    {
      condition: invalidSpecialCharacters(saveQueryTitle, [' '], true),
      errorText: 'Cannot contain special characters',
    },
    {
      condition: invalidStartEndCharacters(saveQueryTitle, [' ']),
      errorText: 'Cannot start or end with whitespace',
    },
    {
      condition: !!getQueriesData?.find((item) => item.querytitle === saveQueryTitle),
      errorText: 'Query name already exists',
    },
    {
      condition: saveQueryTitle.length > 50,
      errorText: 'Cannot be more than 50 characters',
    },
  ])

  return (
    <>
      {getQueriesStatus === 'loading' && <Loader />}
      <Button
        className={`text-primary underline normal-case text-lg ${!isSaveEnabled ? 'opacity-50' : ''}`}
        disabled={!isSaveEnabled || getQueriesStatus === 'loading'}
        variant="text"
        onClick={handleOpen}
      >
        Save Query
      </Button>

      {saveOpen && (
        <Modal open={saveOpen} onClose={handleClose} title="Save Query">
          <QueryStatusAlert
            queryStatus={getQuerySaveStatus}
            errorTitle="Could not get queries:"
            errorObj={getQuerySaveError}
            show404Error
          />
          <div className="flex flex-col w-full mt-10 sm:w-72">
            <TextBox
              label="Query Title *"
              placeholder="Enter query title"
              id="query-title"
              outlined
              value={saveQueryTitle}
              onChange={(value) => {
                setSaveQueryTitle(value)
                validateField(validationLogin('saveQueryTitle'), 'trigger')
              }}
              onBlur={() => validateField(validationLogin('saveQueryTitle'), 'trigger')}
              error={!!validateField(validationLogin('saveQueryTitle'))}
              helperText={getFieldErrorText(validationLogin('saveQueryTitle'))}
            />
          </div>
          <div className="flex justify-center flex-row gap-x-7 mb-6 mt-6">
            <PrimaryButton outlined={true} buttonText="Cancel" onClick={handleClose} />
            <PrimaryButton
              outlined={false}
              buttonText="OK"
              onClick={handleSave}
              disabled={!!validateField(validationLogin('saveQueryTitle'), 'fallthrough')}
            />
          </div>
        </Modal>
      )}

      {overrideOpen && (
        <Modal
          open={overrideOpen}
          onClose={handleClose}
          title="Save Query"
          subTitle="You have reached the maximum number of saved queries. You must overwrite an existing query if you wish to continue."
        >
          <QueryStatusAlert
            queryStatus={getDeleteQueryStatus}
            errorObj={getDeleteQueryError}
            errorTitle="Could not overwrite query:"
            show404Error
          />
          <QueryStatusAlert
            queryStatus={getQuerySaveStatus}
            errorObj={getQuerySaveError}
            errorTitle="Could not overwrite query:"
            show404Error
          />
          <div className="flex flex-col w-full mt-2 sm:w-72">
            <div className="flex flex-col w-full sm:w-72">
              <TextBox
                label="Query Title *"
                placeholder="Enter query title"
                id="query-title"
                outlined
                value={saveQueryTitle}
                onChange={(value) => {
                  setSaveQueryTitle(value)
                  validateField(validationLogin('saveQueryTitle'), 'trigger')
                }}
                onBlur={() => validateField(validationLogin('saveQueryTitle'), 'trigger')}
                error={!!validateField(validationLogin('saveQueryTitle'))}
                helperText={getFieldErrorText(validationLogin('saveQueryTitle'))}
              />
            </div>
            <div className="flex flex-col w-full mt-5 sm:w-72">
              <SelectQuery
                label="Query to Overwrite *"
                selectedQueryTitle={selectedQueryTitle}
                setSelectedQueryTitle={setSelectedQueryTitle}
                options={(getQueriesData || []).map((item: any) => ({
                  text: item.querytitle,
                  value: item.querytitle,
                }))}
                status={getQueriesStatus}
                error={getQueriesError}
              />
            </div>
          </div>
          <div className="flex justify-center flex-row gap-x-7 mb-6 mt-6">
            <PrimaryButton outlined={true} buttonText="Cancel" onClick={handleClose} />
            <PrimaryButton
              outlined={false}
              buttonText="OK"
              onClick={handleOverride}
              disabled={!selectedQueryTitle || !!validateField(validationLogin('saveQueryTitle'), 'fallthrough')}
            />
          </div>
        </Modal>
      )}
    </>
  )
}
